import { useState, useEffect, useRef, useCallback } from 'react';
import logger from 'ws-scripts/modules/logger';
import { useMapsApiKey } from '../features/mapsApiKey/mapsApiKeySlice';

const LOG_PREFIX = '[ws-locations-control][useGoogleApiSession]';
const GOOGLE_MAPS_BASE_URL = 'https://maps.googleapis.com/maps/api/js';
const MIN_INPUT_LENGTH = 3;
const MAX_SCRIPT_LOAD_ATTEMPTS = 15;
const SCRIPT_LOAD_INTERVAL_MS = 200;

/**
 * AutocompleteSessionToken Management:
 *
 * 1. Billing Optimization:
 *    - Without a session token, every keystroke triggers a separately billed API call
 *    - With a session token, multiple keystrokes are grouped into one billable session
 *
 * 2. Session Lifecycle:
 *    - Create: When user starts typing (input length > 3)
 *    - Reuse: During continuous typing/refinement of same search
 *    - Reset: When user clears input or selects a place
 *
 * 3. Cost Example:
 *    Without Token: "New York" = 8 separate billing events
 *    With Token: "New York" = 1 billable session
 *
 * @param {string} inputValue - Current search input value
 * @returns {Object} API status and session token
 */
export const useGoogleApiSession = (inputValue) => {
	const [isApiReady, setIsApiReady] = useState(false);
	const sessionTokenRef = useRef(null);
	const apiKey = useMapsApiKey();
	const hasValidInput =
		inputValue && inputValue.trim().length >= MIN_INPUT_LENGTH;

	// Reset function
	const resetSession = useCallback(() => {
		sessionTokenRef.current = null;
	}, []);

	// Handle script loading
	useEffect(() => {
		if (window.google?.maps?.places) {
			setIsApiReady(true);
			return null;
		}

		// Check if script is already in the header
		let script = document.querySelector(
			'script[src*="maps.googleapis.com/maps/api/js"]'
		);
		if (!script) {
			// Create and load the script
			script = document.createElement('script');
			const params = new URLSearchParams({
				language: 'en',
				libraries: 'places,geocoding',
				channel: 'ddc-web-platform',
				key: apiKey || ''
			});
			script.src = `${GOOGLE_MAPS_BASE_URL}?${params.toString()}`;
			script.async = true;
			script.defer = true;
			document.head.appendChild(script);
		}

		// Wait for Google Maps to load (from preload script)
		let attempts = 0;

		const checkGoogleMaps = setInterval(() => {
			attempts += 1;
			if (window.google?.maps) {
				clearInterval(checkGoogleMaps);
				setIsApiReady(true);
			} else if (attempts >= MAX_SCRIPT_LOAD_ATTEMPTS) {
				clearInterval(checkGoogleMaps);
				logger.error(
					`${LOG_PREFIX} Google Maps API failed to load after ${MAX_SCRIPT_LOAD_ATTEMPTS} attempts`
				);
				setIsApiReady(false);
			}
		}, SCRIPT_LOAD_INTERVAL_MS);

		// Cleanup interval on unmount
		// eslint-disable-next-line consistent-return
		return () => {
			clearInterval(checkGoogleMaps);
		};
		// apiKey is a dependency so the effect re-runs if the Redux store updates it
		// after mount. The early exit (`window.google?.maps?.places`) ensures we don't
		// reload the script when it's already present in the DOM.
	}, [apiKey]);

	// Handle session token lifecycle
	useEffect(() => {
		if (!isApiReady) {
			return;
		}

		if (hasValidInput && !sessionTokenRef.current) {
			// Create new session
			sessionTokenRef.current =
				new window.google.maps.places.AutocompleteSessionToken();
		} else if (!hasValidInput) {
			// End session if input is empty or too short
			resetSession();
		}
	}, [hasValidInput, isApiReady, resetSession, inputValue]);

	return {
		isApiReady,
		sessionToken: sessionTokenRef.current,
		resetSession
	};
};
